import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import HelpIcon from '../components/help/HelpIcon';
import { getAdoptionMetrics, getRetentionMetrics, getArpuMetrics } from '../services/api';
import './MetricsPage.css';

// NER-2785 (FE): dashboard de métricas cross-org por vertical (padel vs medical
// baseline) que alimenta la decisión Path B (NER-2781). Consume 3 endpoints:
//   /metrics/padel (adopción) · /metrics/retention (cohorts) · /metrics/arpu.
// Con businessType=all el backend devuelve cada vertical + el combinado (`all`);
// con un vertical concreto, sólo esa clave. Auth: sólo cuentas Nerdo admin (403
// para tenants normales). El vertical padel devuelve ceros hasta el seed NewPort
// (NER-2782); medical ya tiene datos demo en dev.
const VERTICAL_TABS = ['all', 'padel', 'medical'];

// Vertical "en foco" para las 5 KPIs destacadas: con 'all' usamos el combinado.
const focusVertical = (businessType) => (businessType === 'all' ? 'all' : businessType);

const pct = (x) => (typeof x === 'number' ? `${Math.round(x * 100)}%` : '—');
const num = (x) => (typeof x === 'number' ? x.toLocaleString('es-AR') : '—');
const money = (x) => (typeof x === 'number' ? `$${x.toLocaleString('es-AR')}` : '—');
const days = (x) => (typeof x === 'number' ? `${x}` : '—');

function MetricsPage() {
  const { t } = useTranslation();

  const [businessType, setBusinessType] = useState('all');
  const [adoption, setAdoption] = useState(null);
  const [retention, setRetention] = useState(null);
  const [arpu, setArpu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setForbidden(false);
    try {
      const [adoptionRes, retentionRes, arpuRes] = await Promise.all([
        getAdoptionMetrics(businessType),
        getRetentionMetrics(businessType),
        getArpuMetrics(businessType),
      ]);
      setAdoption(adoptionRes?.metrics || {});
      setRetention(retentionRes?.metrics || {});
      setArpu(arpuRes?.metrics || {});
    } catch (err) {
      console.error('Error loading metrics:', err);
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(t('metrics.loadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [businessType, t]);

  useEffect(() => {
    load();
  }, [load]);

  const focus = focusVertical(businessType);
  const focusAdoption = adoption?.[focus];
  const focusRetention = retention?.[focus];
  const focusArpu = arpu?.[focus];

  // Verticales presentes en la respuesta (para las columnas de la comparativa).
  // Con 'all' → padel + medical (+ all combinado); con un vertical concreto, sólo ese.
  const columns = adoption
    ? ['padel', 'medical', 'all'].filter((v) => adoption[v] || retention?.[v] || arpu?.[v])
    : [];

  // 5 KPIs destacadas del vertical en foco (AC5 del padre NER-2783).
  const kpis = [
    {
      key: 'bookingsPerWeek',
      icon: 'event_available',
      label: t('metrics.kpi.bookingsPerWeek'),
      value: num(focusAdoption?.bookingsPerOrgPerWeek),
      hint: t('metrics.kpi.bookingsPerWeekHint'),
    },
    {
      key: 'successRate',
      icon: 'trending_up',
      label: t('metrics.kpi.successRate'),
      value: pct(focusAdoption?.funnel?.successRate),
      hint: t('metrics.kpi.successRateHint'),
    },
    {
      key: 'retention30',
      icon: 'autorenew',
      label: t('metrics.kpi.retention30'),
      value: pct(focusRetention?.retention?.['30']),
      hint: t('metrics.kpi.retention30Hint'),
    },
    {
      key: 'arpu',
      icon: 'payments',
      label: t('metrics.kpi.arpu'),
      value: money(focusArpu?.overall?.arpuMonthlyAvg),
      hint: t('metrics.kpi.arpuHint'),
    },
    {
      key: 'churn',
      icon: 'trending_down',
      label: t('metrics.kpi.churn'),
      value: pct(focusAdoption?.funnel?.cancelRate),
      hint: t('metrics.kpi.churnHint'),
    },
  ];

  // Filas de la tabla comparativa padel vs medical (baseline).
  const rows = [
    {
      label: t('metrics.row.bookingsPerWeek'),
      get: (v) => num(adoption?.[v]?.bookingsPerOrgPerWeek),
    },
    { label: t('metrics.row.activeOrgs'), get: (v) => num(adoption?.[v]?.activeOrgs) },
    { label: t('metrics.row.totalBookings'), get: (v) => num(adoption?.[v]?.totalBookings) },
    { label: t('metrics.row.successRate'), get: (v) => pct(adoption?.[v]?.funnel?.successRate) },
    {
      label: t('metrics.row.completionRate'),
      get: (v) => pct(adoption?.[v]?.funnel?.completionRate),
    },
    { label: t('metrics.row.cancelRate'), get: (v) => pct(adoption?.[v]?.funnel?.cancelRate) },
    {
      label: t('metrics.row.timeToFirst'),
      get: (v) => days(adoption?.[v]?.timeToFirstReservationDays?.median),
    },
    { label: t('metrics.row.cohortSize'), get: (v) => num(retention?.[v]?.cohortSize) },
    { label: t('metrics.row.retention7'), get: (v) => pct(retention?.[v]?.retention?.['7']) },
    { label: t('metrics.row.retention30'), get: (v) => pct(retention?.[v]?.retention?.['30']) },
    { label: t('metrics.row.retention90'), get: (v) => pct(retention?.[v]?.retention?.['90']) },
    { label: t('metrics.row.arpu'), get: (v) => money(arpu?.[v]?.overall?.arpuMonthlyAvg) },
    {
      label: t('metrics.row.revenueProxy'),
      get: (v) => money(arpu?.[v]?.overall?.revenueProxyWindow),
    },
    { label: t('metrics.row.avgTariff'), get: (v) => money(arpu?.[v]?.overall?.avgTariff) },
  ];

  return (
    <div className="list-page metrics-page">
      <div className="page-header">
        <h1>
          {t('metrics.title')}
          <HelpIcon topicKey="metrics.overview" />
        </h1>
      </div>
      <p className="list-page-subtitle">{t('metrics.subtitle')}</p>

      {/* Filtro por vertical (AC4): padel / medical / all */}
      <div className="metrics-tabs" role="tablist" aria-label={t('metrics.verticalFilter')}>
        {VERTICAL_TABS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={businessType === v}
            className={`metrics-tab ${businessType === v ? 'active' : ''}`}
            onClick={() => setBusinessType(v)}
          >
            {t(`metrics.vertical.${v}`)}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      {forbidden ? (
        <div className="card">
          <div className="empty-state metrics-forbidden">
            <span className="material-icons">lock</span>
            <p>{t('metrics.forbidden')}</p>
          </div>
        </div>
      ) : loading ? (
        <div className="card">
          <div className="loading">{t('common.loading')}</div>
        </div>
      ) : (
        <>
          {/* Nota: padel = 0 hasta el seed real (NER-2782) */}
          {focus !== 'medical' && focusAdoption?.totalBookings === 0 && (
            <div className="metrics-note">{t('metrics.padelZeroNote')}</div>
          )}

          {/* 5 KPIs destacadas del vertical en foco */}
          <div className="metrics-kpis">
            {kpis.map((k) => (
              <div key={k.key} className="stat-card metrics-kpi">
                <div className="stat-icon">
                  <span className="material-icons">{k.icon}</span>
                </div>
                <div className="stat-info">
                  <h2>{k.value}</h2>
                  <p>{k.label}</p>
                  <span className="stat-hint">{k.hint}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Tabla comparativa padel vs medical (baseline) — AC3 */}
          <div className="card">
            <h3 className="metrics-table-title">{t('metrics.comparisonTitle')}</h3>
            {columns.length === 0 ? (
              <div className="empty-state">{t('metrics.empty')}</div>
            ) : (
              <div className="metrics-table-scroll">
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th className="metrics-metric-head">{t('metrics.metric')}</th>
                      {columns.map((v) => (
                        <th key={v} className={`metrics-col-head metrics-col-head--${v}`}>
                          {t(`metrics.vertical.${v}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.label}>
                        <th className="metrics-row-head">{r.label}</th>
                        {columns.map((v) => (
                          <td key={v} className="metrics-cell">
                            {r.get(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MetricsPage;
